// src/pages/AttendanceOverview/attendanceOverviewSlice.js
import { createSlice } from "@reduxjs/toolkit";
import {
  fetchCalendar,
  fetchTodaySummary,
  fetchPendingRequests,
  markAttendance,
  regularizeAttendance,
  applyLeave,
  fetchHolidayList,
  addHoliday,
  addRestrictedHoliday,
  fetchAllEmployees,
  fetchShiftReport,
  fetchAttendanceReport,
  fetchTimeSheet,
  fetchApproveAttendanceReport,
  updateAttendanceStatus,
  fetchcheckInStatus,
  remoteMarkAttendance,
  fetchLeaveDetails,
  addLeaveToEmp,
  addNewShift,
  deleteHoliday,
  deleteShift,
  resendEmail,
  fetchHrLocation,
  editHoliday,
  fetchEmployeeShiftDates,
  fetchEmployeeShiftDetails,
  fetchEmployeeAttendanceSummary
} from "./attendanceOverviewThunk";

const initialState = {
  calendarData: null,
  checkInStatus: null,
  checkOutStatus: null,
  attendanceSummary: {},
  employeeAttendanceSummary: null,
  leaveDetailsSummary: {
    totalLeaveTaken: 0,
    remainingLeave: 0,
    leaveSummary: [],
  },
  pendingRequests: { pending: [], attendance: [] },
  holidayList: [],
  employees: [],
  shiftDates: [],
  shiftDatesLoaded: false,
  shiftReport: [],
  attendanceReport: [],
  approveAttendanceReport: [],
  timeSheetReport: [],
  loading: false,
  error: null,
  selectedDate: null,
  shiftDetails: null,
  successMessage: null,
  getHrlocation: null,
};

const attendanceOverviewSlice = createSlice({
  name: "attendanceOverview",
  initialState,
  reducers: {
    setSelectedDate(state, action) {
      state.selectedDate = action.payload;
    },
    clearSuccess(state) {
      state.successMessage = null;
    },
    clearError(state) {
      state.error = null;
    },
    clearApproveAttendanceReport(state) {
      state.approveAttendanceReport = [];
    },
  },
  extraReducers: (builder) => {
    // calendar
    builder
      .addCase(fetchCalendar.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchCalendar.fulfilled, (state, action) => {
        state.loading = false;
        state.calendarData = action.payload;
      })
      .addCase(fetchCalendar.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch calendar";
      });

    // check in status
    builder
      .addCase(fetchcheckInStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchcheckInStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.checkInStatus = action.payload?.data;
      })
      .addCase(fetchcheckInStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch calendar";
      });

    // fetchTodaySummary - reuse fetchTodaySummary action creator (kept in thunk file but not reimported here; handle via wrapper)
    builder
      .addCase(fetchTodaySummary.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchTodaySummary.fulfilled, (state, action) => {
        state.loading = false;
        state.attendanceSummary = action.payload;
      })
      .addCase(fetchTodaySummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch summary";
      });

    builder
      .addCase(fetchEmployeeAttendanceSummary.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchEmployeeAttendanceSummary.fulfilled, (state, action) => {
        state.loading = false;
        state.employeeAttendanceSummary = action.payload;
      })
      .addCase(fetchEmployeeAttendanceSummary.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch employee attendance";
      });

    // fetchLeaveDetails - reuse fetchLeaveDetails action creator (kept in thunk file but not reimported here; handle via wrapper)
    builder
      .addCase(fetchLeaveDetails.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchLeaveDetails.fulfilled, (state, action) => {
        state.loading = false;
        state.leaveDetailsSumary = action.payload;
      })
      .addCase(fetchLeaveDetails.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch summary";
      });

    // pending requests
    builder
      .addCase(fetchPendingRequests.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchPendingRequests.fulfilled, (state, action) => {
        state.loading = false;
        state.pendingRequests = action.payload;
      })
      .addCase(fetchPendingRequests.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch pending requests";
      });

    // markAttendance
    builder
      .addCase(markAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(markAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Marked attendance";
      })
      .addCase(markAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to mark attendance";
      });

    // remotemarkAttendance
    builder
      .addCase(remoteMarkAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(remoteMarkAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Marked attendance";
      })
      .addCase(remoteMarkAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to mark attendance";
      });

    // regularizeAttendance
    builder
      .addCase(regularizeAttendance.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(regularizeAttendance.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage =
          action.payload?.message || "Attendance regularized";
      })
      .addCase(regularizeAttendance.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to regularize attendance";
      });

    // applyLeave
    builder
      .addCase(applyLeave.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(applyLeave.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Leave applied";
      })
      .addCase(applyLeave.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to apply leave";
      });
    // applyLeaveToEmp
    builder
      .addCase(addLeaveToEmp.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addLeaveToEmp.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Leave applied";
      })
      .addCase(addLeaveToEmp.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to apply leave";
      });
    // applyShiftToEmp
    builder
      .addCase(addNewShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addNewShift.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Leave applied";
      })
      .addCase(addNewShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to apply leave";
      });

    // deleteShiftToEmp
    builder
      .addCase(deleteShift.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteShift.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Shift deleted";
      })
      .addCase(deleteShift.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete shift";
      });
    // resendEmail
    builder
      .addCase(resendEmail.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(resendEmail.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Shift deleted";
      })
      .addCase(resendEmail.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to delete shift";
      });

    // deleteHoliday
    builder
      .addCase(deleteHoliday.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(deleteHoliday.fulfilled, (state, action) => {
        state.loading = false;
        state.successMessage = action.payload?.message || "Holiday deleted";
      })
      .addCase(deleteHoliday.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to Holiday delete";
      });

    // holiday list & add
    builder
      .addCase(fetchHolidayList.pending, (state) => {
        state.loading = true;
      })
      .addCase(fetchHolidayList.fulfilled, (state, action) => {
        state.loading = false;
        state.holidayList = action.payload?.data || action.payload || [];
      })
      .addCase(fetchHolidayList.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch holiday list";
      });

    builder
      .addCase(addHoliday.fulfilled, (state, action) => {
        state.successMessage = action.payload?.message || "Holiday added";
      })
      .addCase(editHoliday.fulfilled, (state, action) => {
        state.successMessage = action.payload?.message || "Holiday added";
      })
      .addCase(addRestrictedHoliday.fulfilled, (state, action) => {
        state.successMessage =
          action.payload?.message || "Restricted holiday added";
      });

    // employees
    builder.addCase(fetchAllEmployees.fulfilled, (state, action) => {
      state.employees = action.payload || [];
    });

    // shift report
    builder.addCase(fetchShiftReport.fulfilled, (state, action) => {
      state.shiftReport = action.payload?.data || action.payload || [];
    });

    // employee shift dates
    builder.addCase(fetchEmployeeShiftDates.pending, (state) => {
      state.shiftDatesLoaded = false;
    });

    builder.addCase(fetchEmployeeShiftDates.fulfilled, (state, action) => {
      state.shiftDates = action.payload || [];
      state.shiftDatesLoaded = true;
    });

    // attendance report
    builder.addCase(fetchAttendanceReport.fulfilled, (state, action) => {
      state.attendanceReport = action.payload?.data || action.payload || [];
    });

    // attendance approve report
    builder.addCase(fetchApproveAttendanceReport.fulfilled, (state, action) => {
      state.approveAttendanceReport =
        action.payload?.data || action.payload || [];
    });

    // get hr location 
    builder.addCase(fetchHrLocation.fulfilled, (state, action) => {
      state.getHrlocation =
        action.payload?.data || action.payload || [];
    });

    // time sheet
    builder.addCase(fetchTimeSheet.fulfilled, (state, action) => {
      state.timeSheetReport = action.payload?.data || action.payload || [];
    });

    // approve / update status
    builder.addCase(updateAttendanceStatus.fulfilled, (state, action) => {
      state.successMessage =
        action.payload?.message || "Attendance status updated";
    });
    // employee shift details especially timings
    builder.addCase(fetchEmployeeShiftDetails.fulfilled, (state, action) => {
    state.shiftDetails =
      action.payload || null;
    });
  },
});

export const { setSelectedDate, clearSuccess, clearError } =
  attendanceOverviewSlice.actions;

export default attendanceOverviewSlice.reducer;
